// The meshes of a shaped ceiling, as plain arrays (docs/claude/ceilings.md, *Shaped
// ceilings*). Pure and DOM-free: three.js reads these buffers from the phase
// that first draws a shape in 3D. Every buffer is non-indexed triangles in the
// room's LOCAL plan frame: x, y in mm, z in mm above the room floor. The surface
// is read only through ceiling.ts (heights, normals, UVs, tilings).

import type { Vec2 } from '../shared/types';
import { earClip } from '../shared/triangulate';
import { openLoops } from './meshTopology';
import {
  type CeilingField,
  ceilingRingTris,
  ceilingTris,
  isPlanarCeiling,
  MESH_SAG,
  outerAt,
  profileAlong,
  surfaceNormal,
  surfaceUV,
  underAt,
  wallTopAt,
} from './ceiling';

export interface MeshBuf { pos: number[]; nrm: number[]; uv: number[] }

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const len = (a: Vec2): number => Math.hypot(a.x, a.y);

function area2(poly: readonly Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) { const p = poly[i], q = poly[(i + 1) % poly.length]; a += p.x * q.y - q.x * p.y; }
  return a;
}

/** Every edge of a ring split where either surface changes piece, at its kinks
 *  and at the curved surfaces' angle samples — the ONE subdivision the wall
 *  prisms and the shell's fascia share, so their seams meet vertex for vertex. */
export function subdivideRing(ring: readonly Vec2[], cf: CeilingField, sag = MESH_SAG): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length], L = len(sub(b, a));
    out.push(a);
    if (L < 1e-9) continue;
    const ss = [...profileAlong(cf, a, b, 'outer', sag), ...profileAlong(cf, a, b, 'under', sag)]
      .map((p) => p.s).filter((s) => s > 1e-6 && s < L - 1e-6).sort((p, q) => p - q);
    let last = -Infinity;
    for (const s of ss) { if (s - last < 1e-6) continue; out.push(lerp(a, b, s / L)); last = s; }
  }
  return out;
}

/** Where a piecewise-linear f has its kinks along a → b (parameters in (0, 1)):
 *  one kink is where the two end tangents meet; more are found by halving. The
 *  edge is read in canonical order, so a neighbour sharing it finds the same. */
function kinkParams(f: (p: Vec2) => number, a0: Vec2, b0: Vec2, tol: number, depth = 0): number[] {
  const flip = a0.x > b0.x || (a0.x === b0.x && a0.y > b0.y);
  const a = flip ? b0 : a0, b = flip ? a0 : b0;
  const L = len(sub(b, a));
  if (L < 1e-6) return [];
  const g = (t: number): number => f(lerp(a, b, t));
  const g0 = g(0), g1 = g(1);
  const lin = (t: number): number => g0 + (g1 - g0) * t;
  if ([0.25, 0.5, 0.75].every((t) => Math.abs(g(t) - lin(t)) <= tol)) return [];
  const res = (ts: number[]): number[] => (flip ? ts.map((t) => 1 - t).reverse() : ts);
  if (depth < 12) {
    const h = 1e-7, sL = (g(h) - g0) / h, sR = (g1 - g(1 - h)) / h;
    if (Math.abs(sL - sR) > 1e-12) {
      const t = (g1 - sR - g0) / (sL - sR);
      if (t > 1e-6 && t < 1 - 1e-6 && Math.abs(g(t) - (g0 + sL * t)) <= tol
        && Math.abs(g(t / 2) - (g0 + sL * t / 2)) <= tol && Math.abs(g((1 + t) / 2) - (g1 - sR * (1 - t) / 2)) <= tol) return res([t]);
    }
  }
  if (depth >= 12) return res([0.5]);
  const m = lerp(a, b, 0.5);
  const left = kinkParams(f, a, m, tol, depth + 1).map((t) => t / 2);
  const right = kinkParams(f, m, b, tol, depth + 1).map((t) => 0.5 + t / 2);
  return res([...left, 0.5, ...right]);
}

function withKinks(ring: readonly Vec2[], fns: ReadonlyArray<(p: Vec2) => number>, tol: number): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    out.push(a);
    const ts = [...new Set(fns.flatMap((f) => kinkParams(f, a, b, tol)))].sort((p, q) => p - q);
    let last = -1;
    for (const t of ts) { if (t - last < 1e-9) continue; out.push(lerp(a, b, t)); last = t; }
  }
  return out;
}

/** A plan triangle split until f is linear over each part: along the chord
 *  between the kinks f has on two of its edges. */
function linearParts(tri: [Vec2, Vec2, Vec2], f: (p: Vec2) => number, tol: number, depth = 0): Array<[Vec2, Vec2, Vec2]> {
  const [a, b, c] = tri;
  const fa = f(a), fb = f(b), fc = f(c);
  const plane = (p: Vec2): number => {
    const d = (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
    const l1 = ((p.x - a.x) * (c.y - a.y) - (c.x - a.x) * (p.y - a.y)) / d, l2 = ((b.x - a.x) * (p.y - a.y) - (p.x - a.x) * (b.y - a.y)) / d;
    return fa + l1 * (fb - fa) + l2 * (fc - fa);
  };
  const probes = [lerp(a, b, 0.5), lerp(b, c, 0.5), lerp(c, a, 0.5), { x: (a.x + b.x + c.x) / 3, y: (a.y + b.y + c.y) / 3 }];
  if (depth >= 6 || probes.every((p) => Math.abs(f(p) - plane(p)) <= tol)) return [tri];
  const v = [a, b, c];
  const k = [0, 1, 2].map((i) => { const ts = kinkParams(f, v[i], v[(i + 1) % 3], tol); return ts.length === 1 ? lerp(v[i], v[(i + 1) % 3], ts[0]) : null; });
  const plain = [0, 1, 2].filter((i) => !k[i]);
  let parts: Array<[Vec2, Vec2, Vec2]>;
  if (plain.length === 1) {
    // Kinks on edges o+1 and o+2: the chord between them cuts off corner o+2.
    const o = plain[0], P = k[(o + 1) % 3]!, Q = k[(o + 2) % 3]!;
    const A = v[o], B = v[(o + 1) % 3], C = v[(o + 2) % 3];
    parts = [[P, C, Q], [A, B, P], [A, P, Q]];
  } else {
    let li = 0, best = -1;
    for (let i = 0; i < 3; i++) { const L = len(sub(v[(i + 1) % 3], v[i])); if (L > best) { best = L; li = i; } }
    const p = v[li], q = v[(li + 1) % 3], r = v[(li + 2) % 3], m = lerp(p, q, 0.5);
    parts = [[p, m, r], [m, q, r]];
  }
  return parts.flatMap((t) => (Math.abs(area2(t)) < 1e-9 ? [] : linearParts(ccw(t), f, tol, depth + 1)));
}

const ccw = (t: [Vec2, Vec2, Vec2]): [Vec2, Vec2, Vec2] => (area2(t) < 0 ? [t[0], t[2], t[1]] : t);

function emit(buf: MeshBuf, P: Array<[number, number, number]>, N: Array<[number, number, number]>, UV: Array<[number, number]>): void {
  for (let i = 0; i < 3; i++) { buf.pos.push(...P[i]); buf.nrm.push(...N[i]); buf.uv.push(...UV[i]); }
}

/** Twice the area of a 3D triangle. */
function crossLen(P: ReadonlyArray<readonly [number, number, number]>): number {
  const u = [P[1][0] - P[0][0], P[1][1] - P[0][1], P[1][2] - P[0][2]], v = [P[2][0] - P[0][0], P[2][1] - P[0][1], P[2][2] - P[0][2]];
  return Math.hypot(u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]);
}

/** The unit normal of triangle P0 P1 P2 (right-handed). */
function triNormal(P: Array<[number, number, number]>): [number, number, number] {
  const u = [P[1][0] - P[0][0], P[1][1] - P[0][1], P[1][2] - P[0][2]], v = [P[2][0] - P[0][0], P[2][1] - P[0][1], P[2][2] - P[0][2]];
  const n: [number, number, number] = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
  const l = Math.hypot(...n) || 1;
  return [n[0] / l, n[1] / l, n[2] / l];
}

/** Vertical quads along a ring from `bottom` up to `top`, wound outward. A quad
 *  whose top is at or below its bottom at both ends is left out; one that
 *  crosses is cut where they meet. U = the length along the ring, V = z. */
function sideWalls(buf: MeshBuf, ring: readonly Vec2[], bottom: (p: Vec2) => number, top: (p: Vec2) => number): void {
  const out = area2(ring) > 0;
  let U = 0;
  for (let i = 0; i < ring.length; i++) {
    const p0 = ring[i], q0 = ring[(i + 1) % ring.length], L = len(sub(q0, p0));
    if (L < 1e-9) continue;
    let p = p0, q = q0, Up = U, Uq = U + L;
    let bp = bottom(p), bq = bottom(q), tp = top(p), tq = top(q);
    U += L;
    const hp = tp - bp, hq = tq - bq;
    if (hp <= 1e-9 && hq <= 1e-9) continue;
    if (hp < 0 || hq < 0) {
      const t = hp / (hp - hq), X = lerp(p, q, t), UX = Up + L * t, bx = bottom(X);
      if (hp > 0) { q = X; Uq = UX; bq = bx; tq = bx; } else { p = X; Up = UX; bp = bx; tp = bx; }
    }
    const d = sub(q0, p0), n: [number, number, number] = out ? [d.y / L, -d.x / L, 0] : [-d.y / L, d.x / L, 0];
    const Pb: [number, number, number] = [p.x, p.y, bp], Qb: [number, number, number] = [q.x, q.y, bq];
    const Qt: [number, number, number] = [q.x, q.y, tq], Pt: [number, number, number] = [p.x, p.y, tp];
    const quads: Array<Array<[[number, number, number], [number, number]]>> = out
      ? [[[Pb, [Up, bp]], [Qb, [Uq, bq]], [Qt, [Uq, tq]]], [[Pb, [Up, bp]], [Qt, [Uq, tq]], [Pt, [Up, tp]]]]
      : [[[Pb, [Up, bp]], [Qt, [Uq, tq]], [Qb, [Uq, bq]]], [[Pb, [Up, bp]], [Pt, [Up, tp]], [Qt, [Uq, tq]]]];
    for (const tri of quads) {
      const P = tri.map((v) => v[0]);
      if (crossLen(P) < 1e-9) continue;
      emit(buf, P, [n, n, n], tri.map((v) => v[1]));
    }
  }
}

/**
 * A prism over a LOCAL plan ring (a wall band, a corner band, a partition, a
 * heater wall, a feature band) from `z0` (a height, or a function of the plan
 * point) up to `top(p)`: side quads along the ring subdivided exactly where the
 * ceiling bends (`subdivideRing`) and where `z0` / `top` have kinks; a top cap
 * split where `top` bends and lifted to it — over the whole ring even where the
 * prism has collapsed when `capWhenCollapsed` is set (a feature band the
 * ceiling cuts, so the shell has no hole over it); a bottom cap where z0 is
 * below the top. Flat normals; UVs in mm (sides: along the ring and z; caps:
 * plan x, y).
 */
export function prismUnder(
  ring: readonly Vec2[], z0: number | ((p: Vec2) => number), top: (p: Vec2) => number, cf: CeilingField,
  opts: { sag?: number; capWhenCollapsed?: boolean } = {},
): MeshBuf {
  const buf: MeshBuf = { pos: [], nrm: [], uv: [] };
  const sag = opts.sag ?? MESH_SAG;
  const bottom = typeof z0 === 'number' ? (): number => z0 : z0;
  // A curve is linear only within its chord tolerance: its own samples already
  // keep it there, so only a kink well past it (a pane top meeting the arc) splits.
  const tol = isPlanarCeiling(cf) ? 1e-6 : 2 * sag;
  const sub0 = subdivideRing(ring, cf, sag);
  const r = withKinks(sub0, [bottom, top], tol);
  if (r.length < 3 || Math.abs(area2(r)) < 1e-9) return buf;
  sideWalls(buf, r, bottom, top);
  for (const t0 of ceilingRingTris(cf, r, 'under', sag)) {
    for (const t of linearParts([t0.a, t0.b, t0.c], top, tol)) {
      const c = { x: (t[0].x + t[1].x + t[2].x) / 3, y: (t[0].y + t[1].y + t[2].y) / 3 };
      if (!opts.capWhenCollapsed && !(top(c) > bottom(c) + 1e-6)) continue;
      const P = t.map((p) => [p.x, p.y, top(p)] as [number, number, number]);
      const n = triNormal(P);
      emit(buf, P, [n, n, n], t.map((p) => [p.x, p.y] as [number, number]));
    }
  }
  for (const t0 of earClip(r)) {
    const tc = ccw([t0[0], t0[1], t0[2]]), c = { x: (tc[0].x + tc[1].x + tc[2].x) / 3, y: (tc[0].y + tc[1].y + tc[2].y) / 3 };
    if (!(bottom(c) < top(c) - 0.5)) continue;
    for (const t of linearParts(tc, bottom, tol)) {
      const rt: [Vec2, Vec2, Vec2] = [t[0], t[2], t[1]];
      const P = rt.map((p) => [p.x, p.y, bottom(p)] as [number, number, number]);
      const n = triNormal(P);
      emit(buf, P, [n, n, n], rt.map((p) => [p.x, p.y] as [number, number]));
    }
  }
  return buf;
}

/**
 * The shell: its top over the outer outline at the outer surface, its underside
 * over the inner outline at the underside (normals down), and the fascia round
 * the outer outline from the wall tops up to the roof. The band between the two
 * outlines is capped by the wall prisms. A 0 mm ceiling has no shell.
 */
export function shellMesh(cf: CeilingField): MeshBuf {
  const { outer, under, fascia } = shellParts(cf);
  return { pos: [...outer.pos, ...under.pos, ...fascia.pos], nrm: [...outer.nrm, ...under.nrm, ...fascia.nrm], uv: [...outer.uv, ...under.uv, ...fascia.uv] };
}

/** `shellMesh`'s three parts apart: a clad room's underside is its ceiling's
 *  wood, the roof and the fascia its outside's (docs/claude/woods.md). */
export function shellParts(cf: CeilingField): { outer: MeshBuf; under: MeshBuf; fascia: MeshBuf } {
  const outer: MeshBuf = { pos: [], nrm: [], uv: [] }, under: MeshBuf = { pos: [], nrm: [], uv: [] }, fascia: MeshBuf = { pos: [], nrm: [], uv: [] };
  if (!(cf.thickness > 0)) return { outer, under, fascia };
  for (const t of ceilingTris(cf, 'outer', MESH_SAG)) {
    const v = [t.a, t.b, t.c];
    emit(outer, v.map((p) => [p.x, p.y, outerAt(cf, p)] as [number, number, number]), v.map((p) => surfaceNormal(cf, 'outer', t.piece, p)), v.map((p) => surfaceUV(cf, 'outer', t.piece, p)));
  }
  for (const t of ceilingTris(cf, 'under', MESH_SAG)) {
    const v = [t.a, t.c, t.b];
    emit(under, v.map((p) => [p.x, p.y, underAt(cf, p)] as [number, number, number]),
      v.map((p) => surfaceNormal(cf, 'under', t.piece, p).map((x) => -x || 0) as [number, number, number]), v.map((p) => surfaceUV(cf, 'under', t.piece, p)));
  }
  sideWalls(fascia, subdivideRing(cf.outerRing, cf, MESH_SAG), (p) => wallTopAt(cf, p), (p) => outerAt(cf, p));
  return { outer, under, fascia };
}

/** The band the shell rests on its walls by — from the underside's rim to the
 *  foot of the fascia (or of the roof, where it comes down to the wall top),
 *  over the walls' tops, facing down —, hidden between the walls and the shell.
 *  It closes the shell, so a 3D section fills it where it cuts it
 *  (rendering.md, *The 3D section*). Built on the rims' own vertices, the band
 *  ear-clipped in plan so it never leaves the walls; kept out of `shellMesh`.
 *  Empty when the shell needs none (its rims meet: 0 mm walls) or they are not
 *  the two loops of a band. */
export function shellSeat(cf: CeilingField, parts = shellParts(cf)): MeshBuf {
  const seat: MeshBuf = { pos: [], nrm: [], uv: [] };
  // The rims: what the shell leaves open, a seam between two meshings of one
  // curve (the roof's and the fascia's) closed.
  const open = openLoops([parts.outer, parts.under, parts.fascia]);
  if (!open || open.loops.length !== 2) return seat;
  const { pts, loops } = open;
  type Rim = Vec2 & { id: number };
  const ring = (l: number[]): Rim[] => l.map((id) => ({ x: pts[id][0], y: pts[id][1], id }));
  const [a, b] = loops.map(ring);
  const [out, inn] = Math.abs(area2(a)) >= Math.abs(area2(b)) ? [a, b] : [b, a];
  for (const t of earClip(out, [inn]) as unknown as Array<[Rim, Rim, Rim]>) {
    // Clipped counter-clockwise in plan: turned over, it faces down.
    const P = [t[0], t[2], t[1]].map((r) => pts[r.id]);
    const u = [P[1][0] - P[0][0], P[1][1] - P[0][1], P[1][2] - P[0][2]], w = [P[2][0] - P[0][0], P[2][1] - P[0][1], P[2][2] - P[0][2]];
    const c: [number, number, number] = [u[1] * w[2] - u[2] * w[1], u[2] * w[0] - u[0] * w[2], u[0] * w[1] - u[1] * w[0]];
    const L = Math.hypot(...c);
    if (!(L > 1e-12)) continue;
    const n = c.map((x) => x / L) as [number, number, number];
    emit(seat, P, [n, n, n], [[0, 0], [0, 0], [0, 0]]);
  }
  return seat;
}
